// File types a browser can display directly in an <img>/background-image. These can be previewed
// full-screen from their original file even when no generated preview exists.
const RENDERABLE_IMAGE_TYPES = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg', 'avif', 'ico', 'apng']

// Videos play inline. Only mp4 matches the overlay's <video type="video/mp4"> source.
const PLAYABLE_VIDEO_TYPES = ['mp4']

// Documents can't render in the full-screen image/video overlay, so they keep opening in a new tab.
const DOCUMENT_TYPES = ['pdf', 'doc', 'docx']

const urlExtension = (url) => {
  const path = (url || '').split(/[?#]/)[0]
  const filename = path.substring(path.lastIndexOf('/') + 1)
  const dot = filename.lastIndexOf('.')
  return dot > -1 ? filename.slice(dot + 1).toLowerCase() : ''
}

const hasType = (types, fileTypeName, url) => {
  const type = (fileTypeName || '').toLowerCase()
  return types.includes(type) || types.includes(urlExtension(url))
}

// The file name is authoritative when present; the url extension covers direct/legacy resources.
export const isRenderableImage = (fileTypeName, url) => hasType(RENDERABLE_IMAGE_TYPES, fileTypeName, url)

export const isPlayableVideo = (fileTypeName, url) => hasType(PLAYABLE_VIDEO_TYPES, fileTypeName, url)

export const isDocument = (fileTypeName, url) => hasType(DOCUMENT_TYPES, fileTypeName, url)

// A preview source is { url, videoUrl, reduced }: a still image at `url`, or a `videoUrl` the overlay
// plays inline (in which case `url` is unused). `reduced` drives the lowered-resolution notice.
export const resourcePreviewSource = (resource) => {
  const fileTypeName = resource?.file?.fileType?.name
  const original = resource?.file?.downloadUrl || resource?.resourceUrl

  if (isDocument(fileTypeName, original)) {
    return null
  }

  // Videos play inline from the original, even though they also carry a generated still-frame preview.
  if (isPlayableVideo(fileTypeName, original)) {
    return { reduced: false, url: '', videoUrl: original }
  }

  if (resource?.file?.previewGeneratedAt) {
    const generated = resource.file?.filePreviewRequests
      ?.find(request => request.imageType === 'Preview' && request.generatedAt)?.downloadUrl
    const url = generated || resource.previewUrl
    if (url) {
      return { reduced: true, url, videoUrl: null }
    }
  }

  if (isRenderableImage(fileTypeName, original)) {
    return { reduced: false, url: original, videoUrl: null }
  }

  return null
}

// Same rule for the Related tab's flattened rows.
export const itemPreviewSource = ({
  hasPreview, previewUrl, fileTypeName, downloadUrl,
} = {}) => {
  if (isDocument(fileTypeName, downloadUrl)) {
    return null
  }

  if (isPlayableVideo(fileTypeName, downloadUrl)) {
    return { reduced: false, url: '', videoUrl: downloadUrl }
  }

  if (hasPreview && previewUrl) {
    return { reduced: true, url: previewUrl, videoUrl: null }
  }

  if (isRenderableImage(fileTypeName, downloadUrl)) {
    return { reduced: false, url: downloadUrl, videoUrl: null }
  }

  return null
}

// suit-theme's Image emits `url(<url>)` unquoted, so whitespace, quotes and parens in an original
// file name break the value. Percent-encode just those; leave existing %XX sequences untouched.
export const cssBackgroundUrl = url => (url || '')
  .replace(/[ "'()]/g, char => `%${char.charCodeAt(0).toString(16).toUpperCase()}`)

// The thumbnail for a resource: the generated preview when there is one, otherwise the original if the
// browser can render it (photos); videos fall back to the placeholder rather than an unplayable <img>.
export const resourceThumbnailUrl = (resource) => {
  if (resource?.file?.previewGeneratedAt) {
    return resource.previewUrl
  }

  const source = resourcePreviewSource(resource)
  return source && !source.videoUrl ? source.url : ''
}

// Same for the Related tab's flattened rows.
export const itemThumbnailUrl = ({
  hasPreview, previewUrl, fileTypeName, downloadUrl,
} = {}) => {
  if (hasPreview && previewUrl) {
    return previewUrl
  }

  const source = itemPreviewSource({ hasPreview, previewUrl, fileTypeName, downloadUrl })
  return source && !source.videoUrl ? source.url : ''
}

export default isRenderableImage
